/** InventoryStore — お助けアイテム 4 種の永続化(Phase A)。
 *  Reference: docs/CLAUDE.md §3.3 Skill Honored
 *  - 4 種(freeze/burst/dash/shield)の所持数を localStorage に保存
 *  - 課金で増やせない設計(Phase A:広告 + デイリー + ステージクリア + Color shop)
 */

export type HelperId = 'freeze' | 'burst' | 'dash' | 'shield';

const STORAGE_KEY = 'ck.inventory';
const SHOP_USAGE_KEY = 'ck.inventoryShopUsage'; // { date: 'YYYY-MM-DD', count: number }
const SHOP_DAILY_LIMIT = 3;
const SHOP_PRICE_COLOR = 100;

export const HELPER_IDS: readonly HelperId[] = ['freeze', 'burst', 'dash', 'shield'] as const;

export interface InventorySnapshot {
  freeze: number;
  burst: number;
  dash: number;
  shield: number;
}

const DEFAULT_INVENTORY: InventorySnapshot = {
  // 初回起動でデモプレイ可能な少量を付与
  freeze: 1,
  burst: 1,
  dash: 1,
  shield: 1,
};

export class InventoryStore {
  private state: InventorySnapshot;

  constructor() {
    this.state = this.load();
  }

  private load(): InventorySnapshot {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_INVENTORY };
      const parsed = JSON.parse(raw) as Partial<InventorySnapshot>;
      return {
        freeze: parsed.freeze ?? 0,
        burst: parsed.burst ?? 0,
        dash: parsed.dash ?? 0,
        shield: parsed.shield ?? 0,
      };
    } catch {
      return { ...DEFAULT_INVENTORY };
    }
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* noop(プライベートモード等) */
    }
  }

  snapshot(): InventorySnapshot {
    return { ...this.state };
  }

  count(id: HelperId): number {
    return this.state[id];
  }

  add(id: HelperId, n = 1): void {
    this.state[id] = Math.max(0, this.state[id] + n);
    this.save();
  }

  consume(id: HelperId): boolean {
    if (this.state[id] <= 0) return false;
    this.state[id] -= 1;
    this.save();
    return true;
  }

  /** デイリー login 時に呼ばれる:任意の 1 種に +1 */
  grantDaily(id: HelperId): void {
    this.add(id, 1);
  }

  /** ステージクリア時に呼ばれる:ランダム 1 種に +1(3-star 時のみ呼び出すこと) */
  grantStageClearRandom(): HelperId {
    const id = HELPER_IDS[Math.floor(Math.random() * HELPER_IDS.length)]!;
    this.add(id, 1);
    return id;
  }

  /** 広告 reward:任意の 1 種に +1 */
  grantAdReward(id: HelperId): void {
    this.add(id, 1);
  }

  /** Color shop 購入:1日 SHOP_DAILY_LIMIT 個まで、価格 SHOP_PRICE_COLOR */
  shopBuyAttempt(
    id: HelperId,
    colorBalance: number,
  ): { ok: boolean; reason?: string; newBalance?: number } {
    const today = new Date().toISOString().slice(0, 10);
    let usage: { date: string; count: number } = { date: today, count: 0 };
    try {
      const raw = window.localStorage.getItem(SHOP_USAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { date: string; count: number };
        usage = parsed;
        if (usage.date !== today) usage = { date: today, count: 0 };
      }
    } catch {
      /* noop */
    }
    if (usage.count >= SHOP_DAILY_LIMIT) return { ok: false, reason: 'daily_limit' };
    if (colorBalance < SHOP_PRICE_COLOR) return { ok: false, reason: 'insufficient_color' };
    usage.count += 1;
    try {
      window.localStorage.setItem(SHOP_USAGE_KEY, JSON.stringify(usage));
    } catch {
      /* noop */
    }
    this.add(id, 1);
    return { ok: true, newBalance: colorBalance - SHOP_PRICE_COLOR };
  }

  shopRemainingToday(): number {
    const today = new Date().toISOString().slice(0, 10);
    try {
      const raw = window.localStorage.getItem(SHOP_USAGE_KEY);
      if (!raw) return SHOP_DAILY_LIMIT;
      const usage = JSON.parse(raw) as { date: string; count: number };
      if (usage.date !== today) return SHOP_DAILY_LIMIT;
      return Math.max(0, SHOP_DAILY_LIMIT - usage.count);
    } catch {
      return SHOP_DAILY_LIMIT;
    }
  }
}

export const HELPER_META: Record<HelperId, { name: string; icon: string; descJa: string }> = {
  freeze: { name: 'Freeze Brush', icon: '❄️', descJa: '敵を3秒停止' },
  burst: { name: 'Color Burst', icon: '🎨', descJa: '5×5 即塗り' },
  dash: { name: 'Quick Dash', icon: '⚡', descJa: '2.5秒疾走+無敵' },
  shield: { name: 'Shield Cloak', icon: '🛡️', descJa: '次の失敗を無効' },
};
